/**
 * Klubbspel Theme System
 * ======================
 * Flexibelt temasystem med separata färgteman och bakgrundsmönster.
 * Användare kan kombinera t.ex. "Skog"-färger med "Bordtennis"-bakgrund.
 * 
 * Arkitektur:
 * - themes.ts: Tema-definitioner (tokens) + bakgrundsmönster
 * - index.css: CSS custom properties som konsumerar tokens
 * - store: Lagrar valt tema och bakgrund i localStorage
 * 
 * Användning:
 *   import { themes, setTheme, setBackground } from '@/styles/themes'
 *   setTheme('forest')           // Byter färgtema
 *   setBackground('tableTennis') // Byter bakgrundsmönster
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ThemeTokens {
  name: string
  label: string
  mode: 'light' | 'dark'
  
  // Core colors (OKLCH format for perceptual uniformity)
  background: string
  foreground: string
  card: string
  cardForeground: string
  popover: string
  popoverForeground: string
  
  // Brand colors
  primary: string
  primaryForeground: string
  secondary: string
  secondaryForeground: string
  accent: string
  accentForeground: string
  
  // Semantic colors
  muted: string
  mutedForeground: string
  destructive: string
  destructiveForeground: string
  destructiveMuted: string
  destructiveMutedForeground: string
  success: string
  successForeground: string
  successMuted: string
  successMutedForeground: string
  info: string
  infoForeground: string
  infoMuted: string
  infoMutedForeground: string
  warning: string
  warningForeground: string
  warningMuted: string
  warningMutedForeground: string
  
  // UI colors
  border: string
  input: string
  ring: string
  
  // Layout
  radius: string
}

export interface BackgroundPattern {
  name: string
  label: string
  sport?: string  // Which sport this pattern represents
  css: string     // CSS for the background (gradient, SVG, etc.)
  overlay?: string // Optional overlay for better readability
  preview?: string // Optional preview image for selection cards
}

export interface BackgroundTheme {
  cover: string
  preview: string
}

export interface SportBackgrounds {
  tableTennis: BackgroundTheme
  tennis: BackgroundTheme
  badminton: BackgroundTheme
  chess: BackgroundTheme
  tableHockey: BackgroundTheme
  padel: BackgroundTheme
  geometric: BackgroundTheme
}

export const sportBackgrounds: SportBackgrounds = {
  tableTennis: {
    cover: '/backgrounds/tableTennis/cover.jpg',
    preview: '/backgrounds/tableTennis/preview.jpg',
  },
  tennis: {
    cover: '/backgrounds/tennis/cover.jpg',
    preview: '/backgrounds/tennis/preview.jpg',
  },
  badminton: {
    cover: '/backgrounds/badminton/cover.jpg',
    preview: '/backgrounds/badminton/preview.jpg',
  },
  chess: {
    cover: '/backgrounds/chess/cover.jpg',
    preview: '/backgrounds/chess/preview.jpg',
  },
  tableHockey: {
    cover: '/backgrounds/tableHockey/cover.jpg',
    preview: '/backgrounds/tableHockey/preview.jpg',
  },
  padel: {
    cover: '/backgrounds/padel/cover.jpg',
    preview: '/backgrounds/padel/preview.jpg',
  },
  geometric: {
    cover: '/backgrounds/geometric/cover.jpg',
    preview: '/backgrounds/geometric/preview.jpg',
  },
}

const backgroundUrl = (path: string) => `url("${path}")`

// =============================================================================
// BACKGROUND PATTERNS (Sport-inspired - Using real images)
// =============================================================================

// Using Unsplash images (free to use) with overlay for readability
export const backgrounds: Record<string, BackgroundPattern> = {
  // No pattern - solid color from theme
  none: {
    name: 'none',
    label: 'Ingen',
    css: 'none',
  },
  
  // Table Tennis - Local placeholder
  tableTennis: {
    name: 'tableTennis',
    label: 'Bordtennis',
    sport: 'table_tennis',
    css: backgroundUrl(sportBackgrounds.tableTennis.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.tableTennis.preview),
  },
  
  // Tennis - Local placeholder
  tennis: {
    name: 'tennis',
    label: 'Tennis',
    sport: 'tennis',
    css: backgroundUrl(sportBackgrounds.tennis.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.tennis.preview),
  },
  
  // Badminton - Local placeholder
  badminton: {
    name: 'badminton',
    label: 'Badminton',
    sport: 'badminton',
    css: backgroundUrl(sportBackgrounds.badminton.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.badminton.preview),
  },
  
  // Chess - Local placeholder
  chess: {
    name: 'chess',
    label: 'Schack',
    sport: 'chess',
    css: backgroundUrl(sportBackgrounds.chess.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.chess.preview),
  },
  
  // Table Hockey - Local placeholder
  tableHockey: {
    name: 'tableHockey',
    label: 'Bordshockey',
    sport: 'table_hockey',
    css: backgroundUrl(sportBackgrounds.tableHockey.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.tableHockey.preview),
  },
  
  // Padel - Local placeholder
  padel: {
    name: 'padel',
    label: 'Padel',
    sport: 'padel',
    css: backgroundUrl(sportBackgrounds.padel.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 100%)',
    preview: backgroundUrl(sportBackgrounds.padel.preview),
  },
  
  // Abstract geometric pattern
  geometric: {
    name: 'geometric',
    label: 'Geometrisk',
    css: backgroundUrl(sportBackgrounds.geometric.cover),
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.80) 100%)',
    preview: backgroundUrl(sportBackgrounds.geometric.preview),
  },
  
  // Subtle dots pattern (keep as CSS for performance)
  dots: {
    name: 'dots',
    label: 'Prickar',
    css: 'radial-gradient(circle, rgba(100,100,150,0.15) 2px, transparent 2px)',
    overlay: 'none',
  },
}

// =============================================================================
// THEME DEFINITIONS
// =============================================================================

export const themes: Record<string, ThemeTokens> = {
  // ---------------------------------------------------------------------------
  // DEFAULT - Klubbspel Blue (Professional, trustworthy)
  // ---------------------------------------------------------------------------
  default: {
    name: 'default',
    label: 'Klubbspel Blå',
    mode: 'light',
    background: 'oklch(1 0 0)',
    foreground: 'oklch(0.25 0.08 250)',
    card: 'oklch(0.98 0.01 250)',
    cardForeground: 'oklch(0.25 0.08 250)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0.25 0.08 250)',
    primary: 'oklch(0.45 0.15 250)',
    primaryForeground: 'oklch(1 0 0)',
    secondary: 'oklch(0.96 0.01 250)',
    secondaryForeground: 'oklch(0.25 0.08 250)',
    muted: 'oklch(0.96 0.01 250)',
    mutedForeground: 'oklch(0.556 0.05 250)',
    accent: 'oklch(0.70 0.15 45)',
    accentForeground: 'oklch(1 0 0)',
    destructive: 'oklch(0.577 0.245 27.325)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.96 0.03 27)',
    destructiveMutedForeground: 'oklch(0.45 0.15 27)',
    success: 'oklch(0.65 0.15 145)',
    successForeground: 'oklch(1 0 0)',
    successMuted: 'oklch(0.96 0.03 145)',
    successMutedForeground: 'oklch(0.40 0.12 145)',
    info: 'oklch(0.55 0.15 240)',
    infoForeground: 'oklch(1 0 0)',
    infoMuted: 'oklch(0.96 0.03 240)',
    infoMutedForeground: 'oklch(0.35 0.12 240)',
    warning: 'oklch(0.75 0.15 70)',
    warningForeground: 'oklch(0.25 0.05 70)',
    warningMuted: 'oklch(0.96 0.03 70)',
    warningMutedForeground: 'oklch(0.45 0.12 70)',
    border: 'oklch(0.922 0.01 250)',
    input: 'oklch(0.922 0.01 250)',
    ring: 'oklch(0.45 0.15 250)',
    radius: '0.5rem',
  },

  // ---------------------------------------------------------------------------
  // FOREST - Nature-inspired green (Calm, organic)
  // ---------------------------------------------------------------------------
  forest: {
    name: 'forest',
    label: 'Skogsgrön',
    mode: 'light',
    background: 'oklch(0.98 0.01 145)',
    foreground: 'oklch(0.20 0.05 145)',
    card: 'oklch(0.99 0.005 145)',
    cardForeground: 'oklch(0.20 0.05 145)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0.20 0.05 145)',
    primary: 'oklch(0.45 0.12 145)',
    primaryForeground: 'oklch(1 0 0)',
    secondary: 'oklch(0.95 0.02 145)',
    secondaryForeground: 'oklch(0.25 0.05 145)',
    muted: 'oklch(0.95 0.02 145)',
    mutedForeground: 'oklch(0.50 0.04 145)',
    accent: 'oklch(0.65 0.15 85)',
    accentForeground: 'oklch(0.15 0.05 85)',
    destructive: 'oklch(0.55 0.20 25)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.95 0.03 25)',
    destructiveMutedForeground: 'oklch(0.45 0.15 25)',
    success: 'oklch(0.60 0.15 145)',
    successForeground: 'oklch(1 0 0)',
    successMuted: 'oklch(0.95 0.03 145)',
    successMutedForeground: 'oklch(0.38 0.12 145)',
    info: 'oklch(0.55 0.12 200)',
    infoForeground: 'oklch(1 0 0)',
    infoMuted: 'oklch(0.95 0.03 200)',
    infoMutedForeground: 'oklch(0.35 0.10 200)',
    warning: 'oklch(0.75 0.15 85)',
    warningForeground: 'oklch(0.20 0.05 85)',
    warningMuted: 'oklch(0.95 0.03 85)',
    warningMutedForeground: 'oklch(0.45 0.12 85)',
    border: 'oklch(0.90 0.02 145)',
    input: 'oklch(0.90 0.02 145)',
    ring: 'oklch(0.45 0.12 145)',
    radius: '0.5rem',
  },

  // ---------------------------------------------------------------------------
  // SUNSET - Warm orange/red (Energetic, passionate)
  // ---------------------------------------------------------------------------
  sunset: {
    name: 'sunset',
    label: 'Solnedgång',
    mode: 'light',
    background: 'oklch(0.99 0.01 45)',
    foreground: 'oklch(0.20 0.08 30)',
    card: 'oklch(1 0.005 45)',
    cardForeground: 'oklch(0.20 0.08 30)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0.20 0.08 30)',
    primary: 'oklch(0.60 0.20 35)',
    primaryForeground: 'oklch(1 0 0)',
    secondary: 'oklch(0.96 0.02 45)',
    secondaryForeground: 'oklch(0.25 0.08 30)',
    muted: 'oklch(0.96 0.02 45)',
    mutedForeground: 'oklch(0.55 0.05 30)',
    accent: 'oklch(0.70 0.18 60)',
    accentForeground: 'oklch(0.15 0.05 60)',
    destructive: 'oklch(0.55 0.25 25)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.96 0.04 25)',
    destructiveMutedForeground: 'oklch(0.45 0.18 25)',
    success: 'oklch(0.60 0.15 145)',
    successForeground: 'oklch(1 0 0)',
    successMuted: 'oklch(0.96 0.03 145)',
    successMutedForeground: 'oklch(0.40 0.12 145)',
    info: 'oklch(0.55 0.12 240)',
    infoForeground: 'oklch(1 0 0)',
    infoMuted: 'oklch(0.96 0.03 240)',
    infoMutedForeground: 'oklch(0.35 0.10 240)',
    warning: 'oklch(0.75 0.18 70)',
    warningForeground: 'oklch(0.20 0.05 70)',
    warningMuted: 'oklch(0.96 0.04 70)',
    warningMutedForeground: 'oklch(0.45 0.14 70)',
    border: 'oklch(0.92 0.02 45)',
    input: 'oklch(0.92 0.02 45)',
    ring: 'oklch(0.60 0.20 35)',
    radius: '0.5rem',
  },

  // ---------------------------------------------------------------------------
  // MIDNIGHT - Dark mode (Modern, developer-friendly)
  // ---------------------------------------------------------------------------
  midnight: {
    name: 'midnight',
    label: 'Midnatt',
    mode: 'dark',
    background: 'oklch(0.15 0.02 250)',
    foreground: 'oklch(0.95 0.01 250)',
    card: 'oklch(0.20 0.02 250)',
    cardForeground: 'oklch(0.95 0.01 250)',
    popover: 'oklch(0.18 0.02 250)',
    popoverForeground: 'oklch(0.95 0.01 250)',
    primary: 'oklch(0.65 0.15 250)',
    primaryForeground: 'oklch(0.10 0.02 250)',
    secondary: 'oklch(0.25 0.02 250)',
    secondaryForeground: 'oklch(0.90 0.01 250)',
    muted: 'oklch(0.25 0.02 250)',
    mutedForeground: 'oklch(0.65 0.03 250)',
    accent: 'oklch(0.70 0.15 200)',
    accentForeground: 'oklch(0.10 0.02 200)',
    destructive: 'oklch(0.60 0.22 25)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.25 0.05 25)',
    destructiveMutedForeground: 'oklch(0.80 0.15 25)',
    success: 'oklch(0.65 0.15 145)',
    successForeground: 'oklch(0.10 0.02 145)',
    successMuted: 'oklch(0.25 0.05 145)',
    successMutedForeground: 'oklch(0.75 0.12 145)',
    info: 'oklch(0.65 0.15 240)',
    infoForeground: 'oklch(0.10 0.02 240)',
    infoMuted: 'oklch(0.25 0.05 240)',
    infoMutedForeground: 'oklch(0.80 0.12 240)',
    warning: 'oklch(0.75 0.15 70)',
    warningForeground: 'oklch(0.15 0.05 70)',
    warningMuted: 'oklch(0.25 0.05 70)',
    warningMutedForeground: 'oklch(0.80 0.12 70)',
    border: 'oklch(0.30 0.02 250)',
    input: 'oklch(0.30 0.02 250)',
    ring: 'oklch(0.65 0.15 250)',
    radius: '0.5rem',
  },

  // ---------------------------------------------------------------------------
  // OCEAN - Coastal blue-green (Fresh, calming)
  // ---------------------------------------------------------------------------
  ocean: {
    name: 'ocean',
    label: 'Havsbris',
    mode: 'light',
    background: 'oklch(0.98 0.01 220)',
    foreground: 'oklch(0.20 0.06 220)',
    card: 'oklch(0.99 0.005 220)',
    cardForeground: 'oklch(0.20 0.06 220)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0.20 0.06 220)',
    primary: 'oklch(0.55 0.14 220)',
    primaryForeground: 'oklch(1 0 0)',
    secondary: 'oklch(0.95 0.02 220)',
    secondaryForeground: 'oklch(0.25 0.06 220)',
    muted: 'oklch(0.95 0.02 220)',
    mutedForeground: 'oklch(0.50 0.04 220)',
    accent: 'oklch(0.65 0.12 180)',
    accentForeground: 'oklch(0.15 0.04 180)',
    destructive: 'oklch(0.55 0.22 25)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.95 0.03 25)',
    destructiveMutedForeground: 'oklch(0.45 0.15 25)',
    success: 'oklch(0.60 0.14 160)',
    successForeground: 'oklch(1 0 0)',
    successMuted: 'oklch(0.95 0.03 160)',
    successMutedForeground: 'oklch(0.38 0.12 160)',
    info: 'oklch(0.55 0.14 220)',
    infoForeground: 'oklch(1 0 0)',
    infoMuted: 'oklch(0.95 0.03 220)',
    infoMutedForeground: 'oklch(0.35 0.12 220)',
    warning: 'oklch(0.75 0.15 70)',
    warningForeground: 'oklch(0.20 0.05 70)',
    warningMuted: 'oklch(0.95 0.03 70)',
    warningMutedForeground: 'oklch(0.45 0.12 70)',
    border: 'oklch(0.90 0.02 220)',
    input: 'oklch(0.90 0.02 220)',
    ring: 'oklch(0.55 0.14 220)',
    radius: '0.5rem',
  },

  // ---------------------------------------------------------------------------
  // HIGH CONTRAST - Accessibility (Maximum readability)
  // ---------------------------------------------------------------------------
  highContrast: {
    name: 'highContrast',
    label: 'Hög kontrast',
    mode: 'light',
    background: 'oklch(1 0 0)',
    foreground: 'oklch(0 0 0)',
    card: 'oklch(1 0 0)',
    cardForeground: 'oklch(0 0 0)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0 0 0)',
    primary: 'oklch(0 0 0)',
    primaryForeground: 'oklch(1 0 0)',
    secondary: 'oklch(0.95 0 0)',
    secondaryForeground: 'oklch(0 0 0)',
    muted: 'oklch(0.90 0 0)',
    mutedForeground: 'oklch(0.25 0 0)',
    accent: 'oklch(0 0 0)',
    accentForeground: 'oklch(1 0 0)',
    destructive: 'oklch(0.45 0.30 25)',
    destructiveForeground: 'oklch(1 0 0)',
    destructiveMuted: 'oklch(0.90 0.05 25)',
    destructiveMutedForeground: 'oklch(0.35 0.20 25)',
    success: 'oklch(0.35 0.20 145)',
    successForeground: 'oklch(1 0 0)',
    successMuted: 'oklch(0.90 0.05 145)',
    successMutedForeground: 'oklch(0.25 0.15 145)',
    info: 'oklch(0.35 0.20 240)',
    infoForeground: 'oklch(1 0 0)',
    infoMuted: 'oklch(0.90 0.05 240)',
    infoMutedForeground: 'oklch(0.25 0.15 240)',
    warning: 'oklch(0.50 0.20 70)',
    warningForeground: 'oklch(0 0 0)',
    warningMuted: 'oklch(0.90 0.05 70)',
    warningMutedForeground: 'oklch(0.35 0.15 70)',
    border: 'oklch(0 0 0)',
    input: 'oklch(0 0 0)',
    ring: 'oklch(0 0 0)',
    radius: '0',
  },
}

// =============================================================================
// THEME & BACKGROUND UTILITIES
// =============================================================================

const THEME_STORAGE_KEY = 'klubbspel-theme'
const BACKGROUND_STORAGE_KEY = 'klubbspel-background'

/**
 * Get the current theme name from localStorage
 */
export function getTheme(): string {
  if (typeof window === 'undefined') return 'default'
  return localStorage.getItem(THEME_STORAGE_KEY) || 'default'
}

/**
 * Get the current background pattern name from localStorage
 */
export function getBackground(): string {
  if (typeof window === 'undefined') return 'none'
  return localStorage.getItem(BACKGROUND_STORAGE_KEY) || 'none'
}

/**
 * Set the current theme and apply it to the document
 */
export function setTheme(themeName: string): void {
  if (typeof window === 'undefined') return
  if (!themes[themeName]) {
    // Theme not found, use default silently
    themeName = 'default'
  }
  
  localStorage.setItem(THEME_STORAGE_KEY, themeName)
  applyTheme(themeName)
}

/**
 * Set the current background pattern and apply it
 */
export function setBackground(backgroundName: string): void {
  if (typeof window === 'undefined') return
  if (!backgrounds[backgroundName]) {
    backgroundName = 'none'
  }
  
  localStorage.setItem(BACKGROUND_STORAGE_KEY, backgroundName)
  applyBackground(backgroundName)
}

/**
 * Apply theme CSS variables to the document root
 */
export function applyTheme(themeName: string): void {
  if (typeof window === 'undefined') return
  
  const theme = themes[themeName] || themes.default
  const root = document.documentElement
  
  // Set data-theme attribute for CSS selectors
  root.setAttribute('data-theme', themeName)
  
  // Apply CSS custom properties
  root.style.setProperty('--background', theme.background)
  root.style.setProperty('--foreground', theme.foreground)
  root.style.setProperty('--card', theme.card)
  root.style.setProperty('--card-foreground', theme.cardForeground)
  root.style.setProperty('--popover', theme.popover)
  root.style.setProperty('--popover-foreground', theme.popoverForeground)
  root.style.setProperty('--primary', theme.primary)
  root.style.setProperty('--primary-foreground', theme.primaryForeground)
  root.style.setProperty('--secondary', theme.secondary)
  root.style.setProperty('--secondary-foreground', theme.secondaryForeground)
  root.style.setProperty('--muted', theme.muted)
  root.style.setProperty('--muted-foreground', theme.mutedForeground)
  root.style.setProperty('--accent', theme.accent)
  root.style.setProperty('--accent-foreground', theme.accentForeground)
  root.style.setProperty('--destructive', theme.destructive)
  root.style.setProperty('--destructive-foreground', theme.destructiveForeground)
  root.style.setProperty('--destructive-muted', theme.destructiveMuted)
  root.style.setProperty('--destructive-muted-foreground', theme.destructiveMutedForeground)
  root.style.setProperty('--success', theme.success)
  root.style.setProperty('--success-foreground', theme.successForeground)
  root.style.setProperty('--success-muted', theme.successMuted)
  root.style.setProperty('--success-muted-foreground', theme.successMutedForeground)
  root.style.setProperty('--info', theme.info)
  root.style.setProperty('--info-foreground', theme.infoForeground)
  root.style.setProperty('--info-muted', theme.infoMuted)
  root.style.setProperty('--info-muted-foreground', theme.infoMutedForeground)
  root.style.setProperty('--warning', theme.warning)
  root.style.setProperty('--warning-foreground', theme.warningForeground)
  root.style.setProperty('--warning-muted', theme.warningMuted)
  root.style.setProperty('--warning-muted-foreground', theme.warningMutedForeground)
  root.style.setProperty('--border', theme.border)
  root.style.setProperty('--input', theme.input)
  root.style.setProperty('--ring', theme.ring)
  root.style.setProperty('--radius', theme.radius)
  
  // Set color-scheme for native elements
  root.style.colorScheme = theme.mode
}

/**
 * Preload an image to avoid blocking UI thread
 */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = url
  })
}

/**
 * Extract image URL from CSS background string
 */
function extractImageUrl(css: string): string | null {
  const match = css.match(/url\(["']?([^"')]+)["']?\)/)
  return match ? match[1] : null
}

/**
 * Apply background pattern to the document body
 * Now async to preload images before applying
 */
export async function applyBackground(backgroundName: string): Promise<void> {
  if (typeof window === 'undefined') return
  
  const bg = backgrounds[backgroundName] || backgrounds.none
  const body = document.body
  
  // Set data-background attribute
  body.setAttribute('data-background', backgroundName)
  
  if (backgroundName === 'none' || bg.css === 'none') {
    // Remove background pattern
    body.style.removeProperty('--bg-pattern')
    body.style.removeProperty('--bg-overlay')
    body.style.backgroundImage = 'none'
    body.style.removeProperty('background-attachment')
    body.style.removeProperty('background-size')
    body.style.removeProperty('background-position')
    return
  }

  // Clean CSS strings (remove newlines and extra whitespace)
  const cleanCss = bg.css.replace(/\s+/g, ' ').trim()
  const cleanOverlay = bg.overlay ? bg.overlay.replace(/\s+/g, ' ').trim() : ''
  
  // Extract image URL and preload if it's an image
  const imageUrl = extractImageUrl(cleanCss)
  if (imageUrl && imageUrl.startsWith('/backgrounds/')) {
    try {
      // Preload image before applying to avoid blocking UI thread
      await preloadImage(imageUrl)
    } catch (error) {
      console.warn('Failed to preload background image:', error)
      // Continue anyway - browser will load it naturally
    }
  }
  
  // Apply background pattern (after image is loaded)
  body.style.setProperty('--bg-pattern', cleanCss)
  body.style.setProperty('--bg-overlay', cleanOverlay || 'none')
  
  // Combine overlay and pattern
  if (cleanOverlay && cleanOverlay !== 'none') {
    body.style.backgroundImage = `${cleanOverlay}, ${cleanCss}`
  } else {
    body.style.backgroundImage = cleanCss
  }
  body.style.backgroundAttachment = 'fixed'
  body.style.backgroundPosition = 'center'
  body.style.backgroundSize = backgroundName === 'dots' ? '20px 20px' : 'cover'
}

/**
 * Get a random daily background for non-authenticated users
 * Uses localStorage to persist the same background for 24 hours
 */
export function getDailyRandomBackground(): string {
  const backgrounds = ['tableTennis', 'tennis', 'badminton', 'chess', 'tableHockey', 'padel', 'geometric']
  
  try {
    const stored = localStorage.getItem('klubbspel-daily-bg')
    const now = Date.now()
    
    if (stored) {
      const { background, timestamp } = JSON.parse(stored)
      const dayInMs = 24 * 60 * 60 * 1000
      
      // If less than 24h old, use same background
      if (now - timestamp < dayInMs) {
        return background
      }
    }
    
    // Generate new random background
    const randomBg = backgrounds[Math.floor(Math.random() * backgrounds.length)]
    localStorage.setItem('klubbspel-daily-bg', JSON.stringify({
      background: randomBg,
      timestamp: now
    }))
    
    return randomBg
  } catch (_error) {
    // Fallback if localStorage is unavailable - silent fail
    return 'tableTennis'
  }
}

/**
 * Initialize theme and background on app start
 */
export function initTheme(): void {
  const savedTheme = getTheme()
  const savedBackground = getBackground()
  applyTheme(savedTheme)
  applyBackground(savedBackground)
}

/**
 * Get list of available themes for UI picker
 */
export function getThemeList(): Array<{ name: string; label: string; mode: 'light' | 'dark' }> {
  return Object.values(themes).map(t => ({
    name: t.name,
    label: t.label,
    mode: t.mode,
  }))
}

/**
 * Get list of available background patterns for UI picker
 */
export function getBackgroundList(): Array<{ name: string; label: string; sport?: string }> {
  return Object.values(backgrounds).map(b => ({
    name: b.name,
    label: b.label,
    sport: b.sport,
  }))
}

